/**
 * API Utility
 * Centralized API configuration and utilities
 */

import axios from 'axios';
import config from '../config/app.config';
import { HTTP_STATUS, ERROR_MESSAGES, API_ENDPOINTS } from '../constants';
import { storageUtils } from '../utils/storage';

const normalizeBaseUrl = (base) => (base.endsWith('/') ? base : `${base}/`);
const API_BASE = normalizeBaseUrl(config.api.baseURL);

const api = axios.create({
  baseURL: API_BASE,
  timeout: config.api.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (requestConfig) => {
    const token = storageUtils.getAuthToken();
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    requestConfig.headers['X-Source-Component'] = window.location.pathname;

    if (process.env.NODE_ENV === 'development') {
      console.log(`API Request: ${requestConfig.method?.toUpperCase()} ${requestConfig.baseURL}${requestConfig.url}`);
    }

    return requestConfig;
  },
  (error) => Promise.reject(error),
);

const buildUrl = (endpoint) => {
  if (!endpoint) return API_BASE;
  return endpoint.startsWith('/') ? `${API_BASE.slice(0, -1)}${endpoint}` : `${API_BASE}${endpoint}`;
};

const handleUnauthorized = async (error) => {
  const originalRequest = error.config;
  if (error.response?.status !== HTTP_STATUS.UNAUTHORIZED || originalRequest._retry) {
    throw error;
  }

  originalRequest._retry = true;
  const refreshToken = storageUtils.getItem('refresh_token');
  if (!refreshToken) {
    storageUtils.clearAuth?.();
    storageUtils.removeAuthToken?.();
    storageUtils.removeItem?.('refresh_token');
    window.location.href = '/login';
    throw error;
  }

  try {
    const refreshUrl = buildUrl(API_ENDPOINTS.AUTH.REFRESH);
    const response = await axios.post(refreshUrl, { refresh: refreshToken });
    const { access } = response.data || {};
    if (!access) {
      throw new Error('Missing access token from refresh response');
    }

    storageUtils.setAuthToken(access);
    originalRequest.headers.Authorization = `Bearer ${access}`;
    return api(originalRequest);
  } catch (refreshError) {
    storageUtils.clearAuth?.();
    storageUtils.removeAuthToken?.();
    storageUtils.removeItem?.('refresh_token');
    window.location.href = '/login';
    throw refreshError;
  }
};

api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  async (error) => {
    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
      try {
        return await handleUnauthorized(error);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', error.response?.data || error.message);
    }

    return Promise.reject(error);
  },
);

export const apiUtils = {
  get: async (url, requestConfig = {}) => {
    try {
      const response = await api.get(url, requestConfig);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  post: async (url, data = {}, requestConfig = {}) => {
    try {
      const response = await api.post(url, data, requestConfig);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  put: async (url, data = {}, requestConfig = {}) => {
    try {
      const response = await api.put(url, data, requestConfig);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  patch: async (url, data = {}, requestConfig = {}) => {
    try {
      const response = await api.patch(url, data, requestConfig);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  delete: async (url, requestConfig = {}) => {
    try {
      const response = await api.delete(url, requestConfig);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  upload: async (url, file, onProgress = null) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: onProgress,
      });

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  download: async (url, filename = 'download') => {
    try {
      const response = await api.get(url, { responseType: 'blob' });

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return true;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

const handleApiError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    switch (status) {
      case HTTP_STATUS.BAD_REQUEST:
        return new Error(data.message || ERROR_MESSAGES.BAD_REQUEST);
      case HTTP_STATUS.UNAUTHORIZED:
        return new Error(ERROR_MESSAGES.UNAUTHORIZED);
      case HTTP_STATUS.FORBIDDEN:
        return new Error(ERROR_MESSAGES.FORBIDDEN);
      case HTTP_STATUS.NOT_FOUND:
        return new Error(ERROR_MESSAGES.NOT_FOUND);
      case HTTP_STATUS.CONFLICT:
        return new Error(data.message || ERROR_MESSAGES.CONFLICT);
      case HTTP_STATUS.UNPROCESSABLE_ENTITY:
        return new Error(data.message || ERROR_MESSAGES.UNPROCESSABLE_ENTITY);
      case HTTP_STATUS.INTERNAL_SERVER_ERROR:
        return new Error(ERROR_MESSAGES.SERVER_ERROR);
      default:
        return new Error(data.message || ERROR_MESSAGES.UNKNOWN_ERROR);
    }
  }

  if (error.request) {
    return new Error(ERROR_MESSAGES.NETWORK_ERROR);
  }

  return new Error(error.message || ERROR_MESSAGES.UNKNOWN_ERROR);
};

export const withRetry = async (apiCall, maxRetries = config.api.retryAttempts) => {
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error;
      if (attempt === maxRetries) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, config.api.retryDelay * attempt));
    }
  }

  throw lastError;
};

export default api;
